import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Send, CheckCircle2, XCircle, AlertTriangle, ChevronDown, FileText, MessageSquare, Clock, ArrowRight, RotateCcw, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Fetch helpers
const authHeaders = () => {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
};

const apiCall = async (method, path, body) => {
  const opts = { method, headers: authHeaders() };
  if (body) opts.body = JSON.stringify(body);
  const res = await fetch(`${API}/api${path}`, opts);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'API error');
  }
  return res.json();
};

// ============ SUMMARY CARD ============
const SummaryCard = ({ sheet }) => {
  const s = sheet?.summary;
  if (!s) return null;
  const items = [
    { label: 'Taxable Value', value: s.total_taxable_value, color: 'text-white' },
    { label: 'CGST', value: s.total_cgst, color: 'text-blue-400' },
    { label: 'SGST', value: s.total_sgst, color: 'text-emerald-400' },
    { label: 'IGST', value: s.total_igst, color: 'text-purple-400' },
    { label: 'Cess', value: s.total_cess, color: 'text-amber-400' },
  ];
  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4" data-testid="gst-summary-card">
      <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">Tax Summary</h3>
      <div className="grid grid-cols-5 gap-3">
        {items.map((item) => (
          <div key={item.label} className="text-center">
            <p className="text-xs text-slate-500 mb-1">{item.label}</p>
            <p className={`text-lg font-bold ${item.color}`}>
              {typeof item.value === 'number' ? `₹${item.value.toLocaleString('en-IN')}` : '—'}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============ EDITABLE SECTION TABLE ============
const EditableTable = ({ title, sectionKey, data, columns, onUpdate, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [editIdx, setEditIdx] = useState(null);
  const [editRow, setEditRow] = useState({});
  const [addingNew, setAddingNew] = useState(false);
  const [newRow, setNewRow] = useState({});

  const autoCalcTax = (row) => {
    const taxable = parseFloat(row.taxable_value) || 0;
    const rate = parseFloat(row.rate) || 0;
    const taxAmt = round2(taxable * rate / 100);
    const pos = row.place_of_supply || '';
    // Simple heuristic: if section is b2cs with type OE or inter-state sections, use IGST
    const isInterState = sectionKey === 'b2cl' || sectionKey === 'exp' || (row.type === 'OE');
    if (isInterState) {
      return { ...row, igst: taxAmt, cgst: 0, sgst: 0, invoice_value: round2(taxable + taxAmt) };
    }
    const half = round2(taxAmt / 2);
    return { ...row, cgst: half, sgst: half, igst: 0, invoice_value: round2(taxable + taxAmt) };
  };

  const round2 = (n) => Math.round(n * 100) / 100;

  const handleFieldChange = (row, key, value, setter) => {
    const updated = { ...row, [key]: value };
    // Auto-calc if taxable_value or rate changed
    if (key === 'taxable_value' || key === 'rate') {
      setter(autoCalcTax(updated));
    } else {
      setter(updated);
    }
  };

  const startEdit = (i) => { setEditIdx(i); setEditRow({ ...data[i] }); setAddingNew(false); };
  const cancelEdit = () => { setEditIdx(null); setEditRow({}); };
  const saveEdit = () => { onUpdate(sectionKey, 'update', editIdx, editRow); setEditIdx(null); };
  const deleteRow = (i) => { onUpdate(sectionKey, 'delete', i); setEditIdx(null); };
  const startAdd = () => {
    const empty = {};
    columns.forEach(c => { empty[c.key] = c.type === 'number' ? 0 : ''; });
    setNewRow(empty);
    setAddingNew(true);
    setEditIdx(null);
  };
  const saveNewRow = () => {
    const calcRow = autoCalcTax(newRow);
    onUpdate(sectionKey, 'add', null, calcRow);
    setAddingNew(false);
    setNewRow({});
  };

  const renderInput = (row, col, setter) => {
    const val = row[col.key] ?? '';
    return (
      <input
        type={col.type === 'number' ? 'number' : 'text'}
        step={col.type === 'number' ? '0.01' : undefined}
        value={val}
        onChange={(e) => handleFieldChange(row, col.key, col.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value, setter)}
        className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-xs text-white focus:border-indigo-500 focus:outline-none"
        data-testid={`edit-${col.key}`}
      />
    );
  };

  const isAutoCalcField = (key) => ['cgst', 'sgst', 'igst', 'invoice_value'].includes(key);
  const rowCount = data?.length || 0;

  return (
    <div className="border border-slate-700/50 rounded-lg overflow-hidden" data-testid={`section-${sectionKey}`}>
      <div className="flex items-center justify-between px-4 py-2.5 bg-slate-800/50">
        <button onClick={() => setOpen(!open)} className="flex items-center gap-2 text-left flex-1">
          <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
          <span className="text-sm font-medium text-slate-300">
            {title} <span className="text-slate-500 ml-1">({rowCount})</span>
          </span>
        </button>
        <button onClick={() => { setOpen(true); startAdd(); }}
          className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-indigo-500/15 text-indigo-400 hover:bg-indigo-500/25 transition-colors"
          data-testid={`add-row-${sectionKey}`}>
          + Add
        </button>
      </div>
      {open && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-700/50">
                {columns.map((col) => (
                  <th key={col.key} className="px-3 py-2 text-left text-slate-500 uppercase tracking-wider font-medium whitespace-nowrap">{col.label}</th>
                ))}
                <th className="px-3 py-2 text-slate-500 w-20">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/30">
              {(data || []).map((row, i) => (
                editIdx === i ? (
                  <tr key={i} className="bg-indigo-500/5">
                    {columns.map((col) => (
                      <td key={col.key} className="px-2 py-1.5">
                        {isAutoCalcField(col.key) ? (
                          <span className="text-slate-400 text-xs">{currency(editRow[col.key])}</span>
                        ) : renderInput(editRow, col, setEditRow)}
                      </td>
                    ))}
                    <td className="px-2 py-1.5">
                      <div className="flex gap-1">
                        <button onClick={saveEdit} className="px-2 py-1 rounded bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 text-[10px]" data-testid="save-edit">Save</button>
                        <button onClick={cancelEdit} className="px-2 py-1 rounded bg-slate-500/20 text-slate-400 hover:bg-slate-500/30 text-[10px]">Cancel</button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={i} className="hover:bg-slate-800/30 group">
                    {columns.map((col) => (
                      <td key={col.key} className="px-3 py-2 text-slate-300 whitespace-nowrap">
                        {col.format ? col.format(row[col.key]) : (row[col.key] ?? '—')}
                      </td>
                    ))}
                    <td className="px-3 py-2">
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => startEdit(i)} className="px-1.5 py-0.5 rounded bg-slate-500/20 text-slate-400 hover:bg-slate-500/30 text-[10px]" data-testid={`edit-row-${i}`}>Edit</button>
                        <button onClick={() => deleteRow(i)} className="px-1.5 py-0.5 rounded bg-red-500/15 text-red-400 hover:bg-red-500/25 text-[10px]" data-testid={`delete-row-${i}`}>Del</button>
                      </div>
                    </td>
                  </tr>
                )
              ))}
              {addingNew && (
                <tr className="bg-emerald-500/5">
                  {columns.map((col) => (
                    <td key={col.key} className="px-2 py-1.5">
                      {isAutoCalcField(col.key) ? (
                        <span className="text-slate-400 text-xs">{currency(newRow[col.key])}</span>
                      ) : renderInput(newRow, col, setNewRow)}
                    </td>
                  ))}
                  <td className="px-2 py-1.5">
                    <div className="flex gap-1">
                      <button onClick={saveNewRow} className="px-2 py-1 rounded bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 text-[10px]" data-testid="save-new">Add</button>
                      <button onClick={() => setAddingNew(false)} className="px-2 py-1 rounded bg-slate-500/20 text-slate-400 hover:bg-slate-500/30 text-[10px]">Cancel</button>
                    </div>
                  </td>
                </tr>
              )}
              {rowCount === 0 && !addingNew && (
                <tr><td colSpan={columns.length + 1} className="px-3 py-6 text-center text-slate-500 text-xs">No records. Click + Add to create one.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const currency = (v) => typeof v === 'number' ? `₹${v.toLocaleString('en-IN')}` : '—';

const B2B_COLS = [
  { key: 'gstin', label: 'Buyer GSTIN', type: 'text' },
  { key: 'invoice_number', label: 'Invoice #', type: 'text' },
  { key: 'invoice_date', label: 'Date', type: 'text' },
  { key: 'place_of_supply', label: 'POS', type: 'text' },
  { key: 'taxable_value', label: 'Taxable', type: 'number', format: currency },
  { key: 'rate', label: 'Rate%', type: 'number' },
  { key: 'cgst', label: 'CGST', type: 'number', format: currency },
  { key: 'sgst', label: 'SGST', type: 'number', format: currency },
  { key: 'igst', label: 'IGST', type: 'number', format: currency },
  { key: 'invoice_value', label: 'Value', type: 'number', format: currency },
];
const B2CL_COLS = [
  { key: 'invoice_number', label: 'Invoice #', type: 'text' },
  { key: 'invoice_date', label: 'Date', type: 'text' },
  { key: 'place_of_supply', label: 'POS', type: 'text' },
  { key: 'taxable_value', label: 'Taxable', type: 'number', format: currency },
  { key: 'rate', label: 'Rate%', type: 'number' },
  { key: 'igst', label: 'IGST', type: 'number', format: currency },
  { key: 'invoice_value', label: 'Value', type: 'number', format: currency },
];
const B2CS_COLS = [
  { key: 'type', label: 'Type', type: 'text' },
  { key: 'place_of_supply', label: 'POS', type: 'text' },
  { key: 'taxable_value', label: 'Taxable', type: 'number', format: currency },
  { key: 'rate', label: 'Rate%', type: 'number' },
  { key: 'cgst', label: 'CGST', type: 'number', format: currency },
  { key: 'sgst', label: 'SGST', type: 'number', format: currency },
  { key: 'igst', label: 'IGST', type: 'number', format: currency },
];
const EXP_COLS = [
  { key: 'export_type', label: 'Type', type: 'text' },
  { key: 'invoice_number', label: 'Invoice #', type: 'text' },
  { key: 'invoice_date', label: 'Date', type: 'text' },
  { key: 'port_code', label: 'Port', type: 'text' },
  { key: 'taxable_value', label: 'Taxable', type: 'number', format: currency },
  { key: 'rate', label: 'Rate%', type: 'number' },
  { key: 'igst', label: 'IGST', type: 'number', format: currency },
  { key: 'invoice_value', label: 'Value', type: 'number', format: currency },
];
const CDNR_COLS = [
  { key: 'gstin', label: 'Buyer GSTIN', type: 'text' },
  { key: 'note_number', label: 'Note #', type: 'text' },
  { key: 'note_date', label: 'Date', type: 'text' },
  { key: 'note_type', label: 'C/D', type: 'text' },
  { key: 'taxable_value', label: 'Taxable', type: 'number', format: currency },
  { key: 'rate', label: 'Rate%', type: 'number' },
  { key: 'cgst', label: 'CGST', type: 'number', format: currency },
  { key: 'sgst', label: 'SGST', type: 'number', format: currency },
  { key: 'igst', label: 'IGST', type: 'number', format: currency },
  { key: 'note_value', label: 'Value', type: 'number', format: currency },
];
const HSN_COLS = [
  { key: 'hsn_code', label: 'HSN Code', type: 'text' },
  { key: 'description', label: 'Description', type: 'text' },
  { key: 'uqc', label: 'UQC', type: 'text' },
  { key: 'total_quantity', label: 'Qty', type: 'number' },
  { key: 'taxable_value', label: 'Taxable', type: 'number', format: currency },
  { key: 'cgst', label: 'CGST', type: 'number', format: currency },
  { key: 'sgst', label: 'SGST', type: 'number', format: currency },
  { key: 'igst', label: 'IGST', type: 'number', format: currency },
];

// ============ READ-ONLY TABLE (State C — Sent to Client) ============
const ReadOnlyTable = ({ title, data, columns }) => {
  const [open, setOpen] = useState(false);
  const rows = data || [];
  if (rows.length === 0) return null;
  return (
    <div className="border border-slate-700/50 rounded-lg overflow-hidden">
      <button onClick={() => setOpen(!open)} className="flex items-center justify-between w-full px-4 py-2.5 bg-slate-800/50">
        <span className="flex items-center gap-2 text-sm font-medium text-slate-300">
          <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
          {title} <span className="text-slate-500 ml-1">({rows.length})</span>
        </span>
      </button>
      {open && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-700/50">
                {columns.map((col) => (
                  <th key={col.key} className="px-3 py-2 text-left text-slate-500 uppercase tracking-wider font-medium whitespace-nowrap">{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/30">
              {rows.map((row, i) => (
                <tr key={i} className="hover:bg-slate-800/30">
                  {columns.map((col) => (
                    <td key={col.key} className="px-3 py-2 text-slate-300 whitespace-nowrap">
                      {col.format ? col.format(row[col.key]) : (row[col.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ============ FLAGS PANEL ============
const FlagsPanel = ({ flags, onUpdateFlag }) => {
  const [expandedFlag, setExpandedFlag] = useState(null);
  const openCount = flags.filter(f => f.status === 'open').length;
  const addressedCount = flags.length - openCount;

  const handleBulkAction = (status) => {
    flags.filter(f => f.status === 'open').forEach(f => onUpdateFlag(f.id, status));
  };

  const severityIcon = (sev) => {
    if (sev === 'high') return <AlertTriangle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />;
    if (sev === 'medium') return <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />;
    return <AlertTriangle className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />;
  };

  return (
    <div data-testid="flags-panel" className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-wider flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> Flags ({openCount} open)
        </h3>
        <span className="text-xs text-slate-500" data-testid="flags-counter">{addressedCount}/{flags.length} done</span>
      </div>

      {/* Bulk actions when flags are open */}
      {openCount > 1 && (
        <div className="flex gap-2 mb-3 pb-3 border-b border-slate-700/50">
          <button onClick={() => handleBulkAction('resolved')}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded text-xs bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 transition-colors" data-testid="bulk-resolve-btn">
            <CheckCircle2 className="w-3 h-3" /> Resolve All
          </button>
          <button onClick={() => handleBulkAction('ignored')}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded text-xs bg-slate-500/15 text-slate-400 hover:bg-slate-500/25 transition-colors" data-testid="bulk-ignore-btn">
            Ignore All
          </button>
        </div>
      )}

      {flags.length === 0 ? (
        <div className="flex items-center gap-2 py-3 text-sm text-emerald-400">
          <CheckCircle2 className="w-4 h-4" /> No flags — sheet is clean
        </div>
      ) : (
        <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
          {flags.map((flag) => {
            const isExpanded = expandedFlag === flag.id;
            const isOpen = flag.status === 'open';
            return (
              <div key={flag.id} className={`rounded-lg border text-xs transition-all ${
                !isOpen && flag.status === 'resolved' ? 'bg-emerald-500/5 border-emerald-500/20' :
                !isOpen && flag.status === 'ignored' ? 'bg-slate-500/5 border-slate-500/20 opacity-40' :
                !isOpen ? 'bg-amber-500/5 border-amber-500/20' :
                flag.severity === 'high' ? 'bg-red-500/5 border-red-500/20' :
                flag.severity === 'medium' ? 'bg-amber-500/5 border-amber-500/20' :
                'bg-slate-500/5 border-slate-500/20'
              }`} data-testid={`flag-card-${flag.id}`}>
                {/* Flag header — always visible */}
                <div className="flex items-start gap-2 p-3 cursor-pointer" onClick={() => setExpandedFlag(isExpanded ? null : flag.id)}>
                  {severityIcon(flag.severity)}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{flag.transaction_ref || flag.id}</p>
                    <p className="text-slate-400 mt-0.5 line-clamp-2">{flag.issue}</p>
                  </div>
                  {!isOpen && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded flex-shrink-0 ${
                      flag.status === 'resolved' ? 'bg-emerald-500/20 text-emerald-400' :
                      flag.status === 'ignored' ? 'bg-slate-500/20 text-slate-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>{flag.status}</span>
                  )}
                  <ChevronDown className={`w-3.5 h-3.5 text-slate-500 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </div>

                {/* Expanded details + actions */}
                {isExpanded && (
                  <div className="px-3 pb-3 pt-0 border-t border-slate-700/30 mt-0">
                    {flag.suggested_fix && (
                      <p className="text-indigo-400 mt-2 mb-2 leading-relaxed">Suggested fix: {flag.suggested_fix}</p>
                    )}
                    {isOpen && (
                      <div className="flex gap-1.5 mt-2">
                        <button onClick={(e) => { e.stopPropagation(); onUpdateFlag(flag.id, 'resolved'); }}
                          className="flex items-center gap-1 px-2 py-1 rounded bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 transition-colors" data-testid={`resolve-flag-${flag.id}`}>
                          <CheckCircle2 className="w-3 h-3" /> Resolved
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); onUpdateFlag(flag.id, 'needs_correction'); }}
                          className="flex items-center gap-1 px-2 py-1 rounded bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors" data-testid={`correction-flag-${flag.id}`}>
                          <XCircle className="w-3 h-3" /> Needs Fix
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); onUpdateFlag(flag.id, 'ignored'); }}
                          className="flex items-center gap-1 px-2 py-1 rounded bg-slate-500/20 text-slate-400 hover:bg-slate-500/30 transition-colors" data-testid={`ignore-flag-${flag.id}`}>
                          — Ignore
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============ CHAT PANEL ============
const ChatPanel = ({ messages, onSend, sending }) => {
  const [input, setInput] = useState('');
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    if (!input.trim() || sending) return;
    onSend(input.trim());
    setInput('');
  };

  // Filter to show only user-initiated chat (skip initial preparation messages)
  const chatMessages = messages.filter((_, i) => i >= 2);

  return (
    <div data-testid="chat-panel">
      <h3 className="text-sm font-semibold text-indigo-400 uppercase tracking-wider mb-3 flex items-center gap-2">
        <MessageSquare className="w-4 h-4" /> Chat with Filing Agent
      </h3>
      <div className="bg-slate-900/50 rounded-lg border border-slate-700/50 max-h-[250px] overflow-y-auto p-3 mb-2 min-h-[100px]">
        {chatMessages.length === 0 ? (
          <p className="text-slate-600 text-xs text-center py-4">Send a message to correct or ask about the sheet</p>
        ) : (
          <div className="space-y-3">
            {chatMessages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-lg px-3 py-2 text-xs ${
                  msg.role === 'user' ? 'bg-indigo-600/30 text-indigo-100' : 'bg-slate-800 text-slate-300'
                }`}>
                  <p className="whitespace-pre-wrap">{msg.content?.substring(0, 2000)}{msg.content?.length > 2000 ? '...' : ''}</p>
                </div>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
          placeholder="Correct or ask about the sheet..."
          className="bg-slate-900 border-slate-700 text-white text-sm flex-1"
          disabled={sending}
          data-testid="chat-input"
        />
        <Button size="sm" onClick={handleSend} disabled={sending || !input.trim()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="chat-send-btn">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
};

// ============ MAIN COMPONENT ============
const GSTFilingTab = ({ order, onRefresh }) => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preparing, setPreparing] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendingToClient, setSendingToClient] = useState(false);

  const orderId = order?.order?.id;

  // Load session on mount
  const loadSession = useCallback(async () => {
    if (!orderId) return;
    try {
      const data = await apiCall('GET', `/admin/orders/${orderId}/gst/session`);
      if (data.exists) setSession(data);
      else setSession(null);
    } catch (err) {
      console.error('Failed to load GST session:', err);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { loadSession(); }, [loadSession]);

  // Poll for sheet status
  useEffect(() => {
    if (!jobId || !orderId) return;
    const interval = setInterval(async () => {
      try {
        const data = await apiCall('GET', `/admin/orders/${orderId}/gst/sheet-status/${jobId}`);
        if (data.status === 'ready') {
          clearInterval(interval);
          setPreparing(false);
          setJobId(null);
          await loadSession();
          toast.success('GST sheet ready for review');
        } else if (data.status === 'failed') {
          clearInterval(interval);
          setPreparing(false);
          setJobId(null);
          toast.error(`Sheet preparation failed: ${data.error || 'Unknown error'}`);
        }
      } catch {
        // Keep polling
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [jobId, orderId, loadSession]);

  const handlePrepareSheet = async () => {
    setPreparing(true);
    try {
      const data = await apiCall('POST', `/admin/orders/${orderId}/gst/prepare-sheet`);
      setJobId(data.job_id);
      if (data.status === 'already_processing') {
        toast.info('Sheet is already being prepared');
      } else {
        toast.info('Preparing GST sheet... this may take 30-60 seconds');
      }
    } catch (err) {
      setPreparing(false);
      toast.error(`Failed to start: ${err.message}`);
    }
  };

  const handleChat = async (message) => {
    setSending(true);
    try {
      const data = await apiCall('POST', `/admin/orders/${orderId}/gst/chat`, { message });
      setSession(prev => ({
        ...prev,
        messages: [...(prev?.messages || []),
          { role: 'user', content: message, timestamp: new Date().toISOString() },
          { role: 'assistant', content: data.reply, timestamp: new Date().toISOString() },
        ],
        current_sheet: data.current_sheet || prev?.current_sheet,
        flags: data.flags || prev?.flags,
        rounds_of_revision: (prev?.rounds_of_revision || 0) + 1,
      }));
      if (data.sheet_updated) toast.success('Sheet updated based on correction');
    } catch (err) {
      toast.error(`Chat failed: ${err.message}`);
    } finally {
      setSending(false);
    }
  };

  const handleUpdateFlag = async (flagId, status) => {
    try {
      const data = await apiCall('POST', `/admin/orders/${orderId}/gst/update-flag`, { flag_id: flagId, status });
      setSession(prev => ({ ...prev, flags: data.flags }));
    } catch (err) {
      toast.error(`Failed to update flag: ${err.message}`);
    }
  };

  const handleSendToClient = async () => {
    setSendingToClient(true);
    try {
      await apiCall('POST', `/admin/orders/${orderId}/gst/send-to-client`);
      toast.success('Sheet sent to client for review');
      await loadSession();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(`Failed: ${err.message}`);
    } finally {
      setSendingToClient(false);
    }
  };

  const handleSheetUpdate = async (section, action, rowIndex, rowData) => {
    try {
      const data = await apiCall('POST', `/admin/orders/${orderId}/gst/update-sheet`, {
        section, action, row_index: rowIndex, row_data: rowData,
      });
      setSession(prev => ({ ...prev, current_sheet: data.current_sheet }));
      toast.success(action === 'add' ? 'Record added' : action === 'delete' ? 'Record deleted' : 'Record updated');
    } catch (err) {
      toast.error(`Failed: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  const filingStatus = order?.order?.gst_filing_status;
  const sheet = session?.current_sheet;
  const flags = session?.flags || [];
  const allFlagsAddressed = flags.length === 0 || flags.every(f => f.status !== 'open');

  // ============ STATE A: Before sheet is prepared ============
  if (!session && !preparing) {
    return (
      <div className="space-y-6" data-testid="gst-state-prepare">
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 text-center">
          <FileText className="w-12 h-12 text-indigo-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Prepare GST Filing Sheet</h3>
          <p className="text-slate-400 text-sm mb-6 max-w-md mx-auto">
            The filing agent will analyze uploaded documents and prepare a complete GSTR-1 confirmation sheet with tax calculations and flags.
          </p>

          {/* Context info */}
          <div className="bg-slate-900/50 rounded-lg p-4 mb-6 text-left max-w-md mx-auto space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Company</span><span className="text-white">{order?.company?.company_name || '—'}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">GSTIN</span><span className="text-white font-mono">{order?.company?.gstin || order?.company?.additional_gstins?.[0]?.gstin || '—'}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Period</span><span className="text-white">{order?.order?.filing_period_label || '—'}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Data Type</span><span className="text-white">{order?.company?.gst_data_types?.join(', ') || order?.company?.gst_data_type || '—'}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Files Uploaded</span><span className="text-white">{order?.submissions?.length || 0}</span></div>
          </div>

          <Button onClick={handlePrepareSheet} className="bg-indigo-600 hover:bg-indigo-700 h-12 px-8" data-testid="prepare-sheet-btn">
            <FileText className="w-5 h-5 mr-2" /> Prepare GST Sheet
          </Button>
        </div>
      </div>
    );
  }

  // ============ PREPARING STATE ============
  if (preparing) {
    return (
      <div className="flex flex-col items-center justify-center py-16" data-testid="gst-state-preparing">
        <Loader2 className="w-12 h-12 animate-spin text-indigo-500 mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">Preparing GST Sheet...</h3>
        <p className="text-slate-400 text-sm">Analysing uploaded documents. This may take 30-60 seconds.</p>
      </div>
    );
  }

  // ============ STATE C: Sent to client ============
  if (session?.status === 'sent_to_client' || filingStatus === 'pending_client_confirmation') {
    return (
      <div className="space-y-6" data-testid="gst-state-sent">
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-6 flex items-center gap-4">
          <Clock className="w-8 h-8 text-purple-400 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-white">Sheet Sent to Client</h3>
            <p className="text-slate-400 text-sm">
              Sent on {order?.order?.gst_sheet_sent_at ? new Date(order.order.gst_sheet_sent_at).toLocaleDateString('en-IN') : 'recently'}. Waiting for client confirmation.
            </p>
          </div>
        </div>
        {sheet && <SummaryCard sheet={sheet} />}
        {sheet && (
          <div className="space-y-3">
            <ReadOnlyTable title="B2B — Supplies to Registered Persons" data={sheet.b2b} columns={B2B_COLS} />
            <ReadOnlyTable title="B2CL — Large B2C Inter-state" data={sheet.b2cl} columns={B2CL_COLS} />
            <ReadOnlyTable title="B2CS — Small B2C" data={sheet.b2cs} columns={B2CS_COLS} />
            <ReadOnlyTable title="EXP — Exports" data={sheet.exp} columns={EXP_COLS} />
            <ReadOnlyTable title="HSN Summary" data={sheet.hsn_summary} columns={HSN_COLS} />
          </div>
        )}
      </div>
    );
  }

  // ============ STATE D: Client confirmed ============
  if (session?.status === 'client_confirmed' || filingStatus === 'client_confirmed') {
    const handleDownloadExcel = () => {
      const token = localStorage.getItem('token');
      const url = `${API}/api/admin/orders/${orderId}/gst/download-excel`;
      fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(res => {
          if (!res.ok) throw new Error('Download failed');
          return res.blob();
        })
        .then(blob => {
          const a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = `GSTR1_${session?.company_name || 'Company'}_${session?.filing_period || 'Period'}.xlsx`;
          a.click();
          URL.revokeObjectURL(a.href);
        })
        .catch(() => toast.error('Download failed'));
    };

    return (
      <div className="space-y-4" data-testid="gst-state-confirmed">
        {/* Header */}
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-5 flex items-center gap-4 flex-wrap">
          <CheckCircle2 className="w-8 h-8 text-emerald-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-white">Client Confirmed</h3>
            <p className="text-slate-400 text-sm">
              {session?.company_name} · {session?.filing_period} · Confirmed on {order?.order?.gst_client_confirmed_at ? new Date(order.order.gst_client_confirmed_at).toLocaleDateString('en-IN') : 'recently'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleDownloadExcel} className="bg-indigo-600 hover:bg-indigo-700" data-testid="download-excel-btn">
              <Download className="w-4 h-4 mr-2" /> Download Excel
            </Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="generate-json-btn" disabled>
              <ArrowRight className="w-4 h-4 mr-2" /> Generate GSTN JSON
            </Button>
          </div>
        </div>

        {/* Summary */}
        <SummaryCard sheet={sheet} />

        {/* All section tables — read-only */}
        <div className="space-y-3">
          <EditableTable title="B2B — Supplies to Registered Persons" sectionKey="b2b" data={sheet?.b2b || []} columns={B2B_COLS} onUpdate={handleSheetUpdate} defaultOpen={true} />
          <EditableTable title="B2CL — Large B2C Inter-state" sectionKey="b2cl" data={sheet?.b2cl || []} columns={B2CL_COLS} onUpdate={handleSheetUpdate} />
          <EditableTable title="B2CS — Small B2C" sectionKey="b2cs" data={sheet?.b2cs || []} columns={B2CS_COLS} onUpdate={handleSheetUpdate} />
          <EditableTable title="EXP — Exports" sectionKey="exp" data={sheet?.exp || []} columns={EXP_COLS} onUpdate={handleSheetUpdate} />
          <EditableTable title="CDNR — Credit/Debit Notes (Registered)" sectionKey="cdnr" data={sheet?.cdnr || []} columns={CDNR_COLS} onUpdate={handleSheetUpdate} />
          <EditableTable title="HSN Summary" sectionKey="hsn_summary" data={sheet?.hsn_summary || []} columns={HSN_COLS} onUpdate={handleSheetUpdate} />
        </div>
      </div>
    );
  }

  // ============ STATE B: Sheet ready for review — main working state ============
  const openFlagCount = flags.filter(f => f.status === 'open').length;
  return (
    <div className="space-y-4" data-testid="gst-state-review">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold text-white">GST Sheet Review</h3>
          <p className="text-xs text-slate-500">
            {session?.company_name} · {session?.filing_period} · Model: {session?.model_used} · Revisions: {session?.rounds_of_revision || 0}
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => {
            const token = localStorage.getItem('token');
            fetch(`${API}/api/admin/orders/${orderId}/gst/download-excel`, { headers: { 'Authorization': `Bearer ${token}` } })
              .then(r => r.ok ? r.blob() : Promise.reject())
              .then(blob => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `GSTR1_${session?.company_name}_${session?.filing_period}.xlsx`; a.click(); })
              .catch(() => toast.error('Download failed'));
          }} className="border-slate-700 text-slate-400" data-testid="download-excel-review-btn">
            <Download className="w-4 h-4 mr-1" /> Excel
          </Button>
          <Button size="sm" variant="outline" onClick={handlePrepareSheet} className="border-slate-700 text-slate-400" data-testid="regenerate-btn">
            <RotateCcw className="w-4 h-4 mr-1" /> Regenerate
          </Button>
          <Button size="sm" onClick={handleSendToClient} disabled={!allFlagsAddressed || sendingToClient}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50" data-testid="send-to-client-btn">
            {sendingToClient ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
            Send to Client
          </Button>
        </div>
      </div>

      {/* Flag warning banner */}
      {openFlagCount > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3 flex items-center gap-3" data-testid="flag-warning-banner">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
          <p className="text-sm text-amber-200">
            <span className="font-semibold">{openFlagCount} flag{openFlagCount > 1 ? 's' : ''} need attention.</span>{' '}
            Resolve or ignore all flags to enable "Send to Client".
          </p>
        </div>
      )}

      {/* Tax Summary — always visible at top */}
      <SummaryCard sheet={sheet} />

      {/* Flags section — full width, collapsible */}
      {flags.length > 0 && (
        <FlagsPanel flags={flags} onUpdateFlag={handleUpdateFlag} />
      )}

      {/* Sheet data tables — editable */}
      <div className="space-y-3">
        <EditableTable title="B2B — Supplies to Registered Persons" sectionKey="b2b" data={sheet?.b2b || []} columns={B2B_COLS} onUpdate={handleSheetUpdate} defaultOpen={true} />
        <EditableTable title="B2CL — Large B2C Inter-state" sectionKey="b2cl" data={sheet?.b2cl || []} columns={B2CL_COLS} onUpdate={handleSheetUpdate} />
        <EditableTable title="B2CS — Small B2C" sectionKey="b2cs" data={sheet?.b2cs || []} columns={B2CS_COLS} onUpdate={handleSheetUpdate} />
        <EditableTable title="EXP — Exports" sectionKey="exp" data={sheet?.exp || []} columns={EXP_COLS} onUpdate={handleSheetUpdate} />
        <EditableTable title="CDNR — Credit/Debit Notes (Registered)" sectionKey="cdnr" data={sheet?.cdnr || []} columns={CDNR_COLS} onUpdate={handleSheetUpdate} />
        <EditableTable title="HSN Summary" sectionKey="hsn_summary" data={sheet?.hsn_summary || []} columns={HSN_COLS} onUpdate={handleSheetUpdate} />
      </div>

      {/* Chat panel — at the bottom */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
        <ChatPanel messages={session?.messages || []} onSend={handleChat} sending={sending} />
      </div>
    </div>
  );
};

export default GSTFilingTab;
